import styled from '@emotion/styled'
import { type CustomContentProps, useSnackbar } from 'notistack'
import { forwardRef, memo, type ReactNode, type RefAttributes, useCallback } from 'react'

import type { TrackingAttributes } from '../../../../code-only/lib/posthog-capture-attributes.ts'
import { spacing } from '../../../../foundations/spacing-tokens.ts'
import { Text } from '../../../../foundations/text/index.ts'
import { Avatar } from '../../../avatar/avatar.tsx'
import type { ButtonStyle } from '../../../button/props.ts'
import { type ActionButtonItem, CalloutNotificationTemplate } from '../../common/callout-notification-template.tsx'
import { colorsPerVariant, notificationDefaultIcon, type NotificationVariant } from '../util.ts'

type TemporaryNotificationProps = {
  variant: NotificationVariant
  dark?: boolean
  title?: string
  actionButtons?: ActionButtonItem[] | undefined
  showAvatar?: boolean
  children?: ReactNode
  className?: string | undefined
  onExitClick: () => void
  trackingAttributes?: TrackingAttributes | undefined
  exitTrackingId?: string | undefined
  // The 'forwardRef' is mandatory to use it as a custom component for notistack 'Snackbar'
} & RefAttributes<HTMLDivElement>

const notificationVariantToButtonStyle: Record<NotificationVariant, ButtonStyle> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
}

export const TemporaryNotification = memo<TemporaryNotificationProps>(
  forwardRef<HTMLDivElement, TemporaryNotificationProps>(function TemporaryNotification(props, ref) {
    const {
      variant,
      title,
      dark,
      actionButtons,
      showAvatar = true,
      children,
      className,
      onExitClick,
      trackingAttributes,
      exitTrackingId,
    } = props

    const colors = colorsPerVariant[variant][dark ? 'dark' : 'light']

    return (
      <CalloutNotificationTemplate
        {...colors}
        leftAvatar={showAvatar ? <Avatar icon={notificationDefaultIcon[variant]} /> : undefined}
        leftAvatarSize="30"
        actionButtons={actionButtons}
        buttonStyle={notificationVariantToButtonStyle[variant]}
        trackingAttributes={trackingAttributes}
        rightAvatarTrackingId={exitTrackingId}
        title={title}
        className={className}
        rightAvatar={<Avatar icon="xmark-regular" />}
        onRightAvatarClick={onExitClick}
        ref={ref}
        shadow={true}
        indented={true}
      >
        {children}
      </CalloutNotificationTemplate>
    )
  }),
)

type TemporaryNotificationAlertOrusProps = {
  orusVariant: NotificationVariant
  extra?: ReactNode | undefined
  actionButtons?: ActionButtonItem[] | undefined
}

type TemporaryNotificationAlertProps = CustomContentProps & TemporaryNotificationAlertOrusProps

// notistack expects custom alerts to:
// - forward the ref
// - be a single element (no fragment)
export const TemporaryNotificationAlert = memo(
  forwardRef<HTMLDivElement, TemporaryNotificationAlertProps>(function TemporaryNotificationAlert(props, ref) {
    // Props passed by notistack
    const { id, message } = props
    const { orusVariant, actionButtons } = props
    const { closeSnackbar } = useSnackbar()
    const handleCloseSnackbar = useCallback(() => closeSnackbar(id), [closeSnackbar, id])

    return (
      <TemporaryNotification
        ref={ref}
        onExitClick={handleCloseSnackbar}
        variant={orusVariant}
        actionButtons={actionButtons}
      >
        <TemporaryNotificationAlertContainer>
          <Text variant="body2">{message}</Text>

          {props.extra}
        </TemporaryNotificationAlertContainer>
      </TemporaryNotification>
    )
  }),
)

const TemporaryNotificationAlertContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing[40]};
`
